import React from 'react';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import DonutCard from './charts/DonutCard';
import RateCard from './charts/RateCard';
import RecentJoinersList from './charts/RecentJoinersList';
import BigPercentCard from './charts/BigPercentCard';
import ActivityBarChart from './charts/ActivityBarChart';
import { PLANE, PLANE_META } from '../../utils/dashboardFeatures';

const ACCENT = PLANE_META[PLANE.SHOORAH].accent;
const PEOPLE_ACCENT = PLANE_META[PLANE.PEOPLE].accent;

// Cards rise in together but staggered, so the band assembles top-left to
// bottom-right rather than flashing in as one block.
const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.07 } }
};

const item = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { duration: 0.35, ease: 'easeOut' } }
};

const percentOf = (part, whole) =>
  Number.isFinite(part) && Number.isFinite(whole) && whole > 0 ? (part / whole) * 100 : null;

/**
 * Analytics band that sits between the People KPI tiles and the feature grids.
 *
 * Everything here is derived from data the API already returns — no invented
 * numbers. Cards that have nothing to show say so rather than rendering an
 * empty chart.
 */
const EngagementBand = ({ people, loading }) => {
  const total = people?.totalUsers ?? null;
  const active = people?.activeUsers ?? null;
  const pending = people?.pendingUsers ?? null;

  const inactive =
    Number.isFinite(total) && Number.isFinite(active) && Number.isFinite(pending)
      ? Math.max(0, total - active - pending)
      : 0;

  const activationRate = percentOf(active, total);
  const awaitingRate = percentOf(pending, total);

  const activity = [
    Number(people?.activeToday) || 0,
    Number(people?.activeThisMonth) || 0,
    Number(people?.activeThisYear) || 0
  ];

  return (
    <motion.div
      variants={container}
      initial="hidden"
      animate="show"
      className="flex flex-col gap-3"
    >
      {/* row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <motion.div variants={item}>
          <DonutCard
            title="User breakdown"
            centreLabel="Total"
            emptyLabel="No user data yet"
            segments={[
              { label: 'Active', value: active || 0, color: ACCENT },
              { label: 'Awaiting activation', value: pending || 0, color: PEOPLE_ACCENT },
              { label: 'Inactive', value: inactive, color: '#BBBBBB' }
            ]}
            loading={loading}
          />
        </motion.div>

        <motion.div variants={item} className="flex flex-col gap-3">
          <RateCard
            title="Activation rate"
            rowLabel="Users active"
            percent={activationRate}
            caption={
              Number.isFinite(active) && Number.isFinite(total)
                ? `${active.toLocaleString()} of ${total.toLocaleString()} users`
                : null
            }
            accent={ACCENT}
          />
          <RateCard
            title="Awaiting activation"
            rowLabel="Not yet onboarded"
            percent={awaitingRate}
            caption={Number.isFinite(pending) ? `${pending.toLocaleString()} users invited` : null}
            accent={PEOPLE_ACCENT}
          />
        </motion.div>

        <motion.div variants={item}>
          <RecentJoinersList
            title="Recently joined employees"
            joiners={people?.recentJoiners}
            loading={loading}
          />
        </motion.div>
      </div>

      {/* row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
        <motion.div variants={item} className="lg:col-span-1">
          <BigPercentCard
            label="Percentage of users not yet active"
            percent={awaitingRate}
            caption="Invited but never signed in"
          />
        </motion.div>

        <motion.div variants={item} className="lg:col-span-3">
          <ActivityBarChart
            title="Active users"
            subtitle="Across reporting windows"
            categories={['Today', 'This month', 'This year']}
            data={activity}
            accent={ACCENT}
            loading={loading}
          />
        </motion.div>
      </div>
    </motion.div>
  );
};

EngagementBand.propTypes = {
  people: PropTypes.object,
  loading: PropTypes.bool
};

export default EngagementBand;
